import { stripLanguagePrefix } from '@/lib/url-language-utils'

const ROOM_PATH_PATTERN = /(^|\s)(\/(?:[^\s/?#]+\/)?room\/[\w-]+(?:[?#]\S*)?)(?=\s|$)/g
const ABSOLUTE_URL_PATTERN = /https?:\/\/\S+/gi

const MENTISS_ROOM_HOSTS = new Set([
  'mentiss.ai',
  'www.mentiss.ai',
  'werewolf.mentiss.ai',
  'b-app.mentiss.ai',
  'b-werewolf.mentiss.ai',
])

export type ParsedRoomInvite = {
  roomCode: string
  caption?: string
}

function isAllowedRoomHost(
  hostname: string,
  currentHostname?: string,
): boolean {
  const normalized = hostname.toLowerCase()
  if (MENTISS_ROOM_HOSTS.has(normalized))
    return true
  if (currentHostname && normalized === currentHostname.toLowerCase())
    return true
  return normalized === 'localhost' || normalized === '127.0.0.1'
}

function roomCodeFromUrl(
  rawUrl: string,
  currentOrigin?: string,
): string | null {
  try {
    const base = currentOrigin ?? 'https://werewolf.mentiss.ai'
    const url = new URL(rawUrl, base)
    const currentHostname = currentOrigin
      ? new URL(currentOrigin).hostname
      : undefined
    if (!isAllowedRoomHost(url.hostname, currentHostname))
      return null
    const match = stripLanguagePrefix(url.pathname).match(/^\/room\/([\w-]+)\/?$/)
    return match?.[1] ?? null
  }
  catch {
    return null
  }
}

function normalizedCaption(text: string): string | undefined {
  const caption = text.replaceAll(/\s+/g, ' ').trim()
  return caption || undefined
}

export function parseRoomInvite(
  rawText: string,
  currentOrigin?: string,
): ParsedRoomInvite | null {
  const text = rawText.trim()
  if (!text)
    return null

  const relativeMatches = [...text.matchAll(ROOM_PATH_PATTERN)]
    .map(match => ({
      rawUrl: match[2],
      index: (match.index ?? 0) + match[1].length,
      roomCode: roomCodeFromUrl(match[2], currentOrigin),
    }))
    .filter(
      (match): match is { rawUrl: string, index: number, roomCode: string } =>
        !!match.roomCode,
    )
  const absoluteMatches = [...text.matchAll(ABSOLUTE_URL_PATTERN)]
    .map(match => ({ rawUrl: match[0], index: match.index ?? 0 }))
    .map(match => ({
      ...match,
      roomCode: roomCodeFromUrl(match.rawUrl, currentOrigin),
    }))
    .filter(
      (match): match is { rawUrl: string, index: number, roomCode: string } =>
        !!match.roomCode,
    )

  const roomMatches = [
    ...relativeMatches,
    ...absoluteMatches,
  ]
  if (roomMatches.length !== 1)
    return null

  const match = roomMatches[0]
  const caption = normalizedCaption(
    `${text.slice(0, match.index)} ${text.slice(match.index + match.rawUrl.length)}`,
  )
  return {
    roomCode: match.roomCode,
    ...(caption ? { caption } : {}),
  }
}
